import { ixToast } from "@shared/src/utils/ixToast";

export type ErrorResponse<T = any> = {
  data: T | null;
  error: string | null;
};

interface ServerErrorOptions {
  permanent?: boolean;
  successMessage?: string;
  errorMessage?: string;
  onReload?: () => void;
}

// Helper to get error message from any error type
export const getErrorMessage = (error: unknown) => {
  const err = error as any;
  if (("response" in err) as any) {
    return err.response?.data?.message || err.message;
  }
  return error instanceof Error ? error.message : "An unexpected error occurred";
};

// Server-side error handler - now returns a simpler structure
export async function handleServerError<T>(
  action: () => Promise<T>,
  fallbackData?: T,
  options: Partial<ServerErrorOptions> = {},
): Promise<{ data: T | undefined; error: string | null }> {
  try {
    const data = await action();
    return { data, error: null };
  } catch (error) {
    return {
      data: fallbackData,
      error: getErrorMessage(error),
    };
  }
}

// Service error handler - to be used within services
export async function handleServiceError<T>(
  action: () => Promise<T>,
  fallbackData?: T,
  options: Partial<ServerErrorOptions> = {},
): Promise<ErrorResponse<T>> {
  try {
    const data = await action();
    return { data, error: null };
  } catch (error: any) {
    return {
      data: fallbackData ?? null,
      error,
      // error: getErrorMessage(error),
    };
  }
}

// Client-side error handler - for UI components
export function handleClientError(error: string | null, options: Partial<ServerErrorOptions> = {}) {
  if (error) {
    // Default options for client errors - not permanent by default
    const defaultOptions = {
      permanent: false,
    };

    // Merge with provided options
    const finalOptions = { ...defaultOptions, ...options };

    // Show toast notification
    ixToast.custom({
      variant: "destructive",
      title: "Error",
      description: finalOptions.errorMessage || error,
      permanent: finalOptions.permanent,
      onOpenChange: finalOptions.onReload
        ? (open: boolean) => {
            if (!open && finalOptions.onReload) {
              finalOptions.onReload();
            }
          }
        : undefined,
    });
  } else if (options.successMessage) {
    ixToast.success(options.successMessage);
  }
}
